import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty, omit } from 'lodash';

import usePrevious from '../../../../utils/react/usePrevious';
import useUpdatedRef from '../../../../utils/react/useUpdatedRef';
import { getSheetsState } from '../sheetSelectors';
import { SheetStateByKey } from '../sheetTypes';
import { getSheetSnapPointWindowHeight } from '../utils/sheetHeightUtils';

type SheetObstructionHandlerArgs = {
    sheetKey?: string;
    isOpen: boolean;
    activeSnapPoint: number;
    snapPoints?: number[];
    onSnapPointChange: (activeSnapPoint: number) => void;
};

/**
 * Keeps a sheet's content clear of whatever is covering the bottom of the screen — the keyboard, or
 * another sheet opening on top of it — by driving the sheet to a taller snap point and back again.
 *
 * This lives in the app rather than in Roma because it reads the state of *every* open sheet.
 */
export default ({ sheetKey, isOpen, activeSnapPoint, snapPoints, onSnapPointChange }: SheetObstructionHandlerArgs) => {
    const sheetsState = useSelector(getSheetsState);
    const prevSheetsState = usePrevious(sheetsState);

    const activeSnapPointRef = useUpdatedRef(activeSnapPoint);
    const returnSnapPoint = useRef<number | null>(null);

    /**
     * On obstruction added (e.g. keyboard shown), adjust the snap points to accommodate the obstruction
     */
    const onObstructionAdded = (height: { px: number } | { point: number }) => {
        const maxSnapPoint = Math.max(...(snapPoints || []), activeSnapPointRef.current);

        // If max snap point >=90% of the window height, adjust the active snap point to
        // that value to account for the obstruction (e.g. keyboard).
        if (maxSnapPoint && maxSnapPoint >= 0.9) {
            onSnapPointChange(maxSnapPoint);
            return;
        }

        // Otherwise, calculate new snap point that will push the fixed height of sheet content
        // to above the obstruction (maxed at 1 snap point).

        const obstructionHeightPoint = 'point' in height ? height.point : height.px / getSheetSnapPointWindowHeight();
        const temporarySnapPoint = Math.min(obstructionHeightPoint + activeSnapPointRef.current, 1);

        returnSnapPoint.current = activeSnapPointRef.current;
        onSnapPointChange(temporarySnapPoint);
    };

    /**
     * On obstruction removed (e.g. keyboard hide), reset the snap points to default state if needed
     */
    const onObstructionRemoved = () => {
        if (!returnSnapPoint.current) return;

        // If the sheet is fixed height, reset the active snap point to the default fixed height

        onSnapPointChange(returnSnapPoint.current);
        returnSnapPoint.current = null;
    };

    /**
     * When another supporting sheet is opened, expand the current sheet to its max snap point
     */
    useEffect(() => {
        if (!isOpen) return;
        if (!sheetKey) return;

        const otherOpenSheets: SheetStateByKey = omit(sheetsState, sheetKey);
        const prevOtherOpenSheets: SheetStateByKey = omit(prevSheetsState, sheetKey);
        const otherSheetsJustOpened = isEmpty(prevOtherOpenSheets) && !isEmpty(otherOpenSheets);
        if (!otherSheetsJustOpened) return;

        const currentSheetContainsEditor = !!sheetsState[sheetKey]?.config?.containsElementEditor;
        if (!currentSheetContainsEditor) return;

        // Get the highest active snap point from other open sheets as the obstruction height point
        const obstructionHeightPoint = Object.values(otherOpenSheets).reduce((acc, sheet) => {
            if (sheet && sheet.activeSnapPoint > acc) return sheet.activeSnapPoint;
            return acc;
        }, 0);

        onObstructionAdded({ point: obstructionHeightPoint });

        return () => {
            onObstructionRemoved();
        };
    }, [sheetsState]);

    /**
     * When the keyboard is shown, expand the sheet to its max snap point
     */
    useEffect(() => {
        if (!isOpen) return;

        // @ts-ignore - This is a custom event with no type definition
        const onKeyboardWillShow = (event: Event) => onObstructionAdded({ px: event.detail?.keyboardHeight || 0 });
        const onKeyboardWillHide = () => onObstructionRemoved();

        window.addEventListener('keyboardWillShow', onKeyboardWillShow);
        window.addEventListener('keyboardWillHide', onKeyboardWillHide);

        return () => {
            window.removeEventListener('keyboardWillShow', onKeyboardWillShow);
            window.removeEventListener('keyboardWillHide', onKeyboardWillHide);
        };
    }, [isOpen]);
};
